import { useMobile } from "@/providers/mobileProvider";
import { Star, CheckSquare, Quote, Heart } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

const testimonials = [
  {
    id: 1,
    name: "Angela",
    contentKey: "socialProof.testimonials.angela",
    avatar: "A",
    image: "/images/cindy.webp",
  },
  {
    id: 3,
    name: "Esmée",
    contentKey: "socialProof.testimonials.esmee",
    avatar: "E",
    image: "/images/esmee.webp",
  },
  {
    id: 6,
    name: "David",
    contentKey: "socialProof.testimonials.david",
    avatar: "D",
    image: "/images/david.webp",
  },
];

const images = [
  "/images/thijs.webp",
  "/images/cindy.webp",
  "/images/angelo.webp",
  "/images/eva.webp",
  "/images/christine.webp",
];

export function ProgressSocialProof() {
  const { isMobile } = useMobile();
  const { t } = useTranslation();

  // only show 3 testimonials when isMobile
  const testimonialsToShow = isMobile ? testimonials.slice(0, 3) : testimonials;

  return (
    <section id="social-proof" data-testid="social-proof" className="col-span-12 md:mb-16">
      {/* Header */}
      <div className="text-center mb-8 md:mb-12">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 px-4 py-2 rounded-full border border-primary/20 mb-4 md:mb-6">
          <Heart className="size-4 text-secondary fill-secondary" />
          <span className="text-sm font-medium text-primary">
            {t("socialProof.lovedByCommunity")}
          </span>
        </div>
        <Heading level="h2" size="h2">
          {t("socialProof.title")}
        </Heading>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
          {t("socialProof.description")}
        </p>
      </div>

      {/* Testimonials */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 px-4 md:px-0">
        {testimonialsToShow.map((testimonial, index) => (
          <div
            key={testimonial.id}
            className={`h-fit group relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-background via-background to-muted/10 border border-border/40 p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:border-primary/30 hover:-translate-y-1 ${
              index === 1 ? "md:col-span-1 xl:col-span-1" : ""
            }`}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Quote icon */}
            <div className="relative mb-4 md:mb-6">
              <Quote className="size-6 md:size-8 text-primary/20 group-hover:text-primary/40 transition-colors duration-300" />
            </div>

            {/* Testimonial content */}
            <blockquote className="relative mb-6 md:mb-8">
              <p className="text-foreground/90 leading-relaxed text-sm md:text-base">
                "{t(testimonial.contentKey)}"
              </p>
            </blockquote>

            {/* Author section */}
            <div className="relative flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <img
                    src={testimonial.image}
                    alt={`${testimonial.name} profile`}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-primary/20"
                  />
                </div>

                {/* Author info */}
                <div className="flex flex-col">
                  <div className="font-semibold text-foreground text-sm sm:text-base">
                    {testimonial.name}
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-1 mt-1 sm:mt-2">
                    {[1, 2, 3, 4, 5].map((starId) => (
                      <Star
                        key={`star-${testimonial.id}-${starId}`}
                        className="size-3 sm:size-4 fill-amber-400 stroke-amber-400"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA or additional social proof */}
      <div className="text-center mt-8 md:mt-12 px-4 md:px-0">
        <div className="inline-flex flex-col sm:flex-row items-center gap-2 text-sm text-muted-foreground">
          <div className="flex -space-x-2">
            {images.map((image) => (
              <img
                key={image}
                src={image}
                alt={`${image} profile`}
                className="w-8 h-8 rounded-full object-cover border-2 border-background"
              />
            ))}
          </div>
          <span className="text-center sm:text-left">{t("socialProof.joinGrowingCommunity")}</span>
        </div>
      </div>
    </section>
  );
}
